import React, { useState } from 'react';
import { Package, ShoppingCart, Trash2, Tag } from 'lucide-react';
import MagasinBadge from '../../components/MagasinBadge';
import { LineBadge } from './PosV2CatalogGrid';
import { fmt } from './posV2Utils';
import { stockPhotoUrl } from './posV2PhotoUtils';

/**
 * Ligne de panier POS V2. Bouton « Rabais » = rabais produit dégradé : montant libre saisi par le
 * vendeur, SANS blocage ni plafond (canal distinct de la remise). Le prix net est appliqué et le
 * rabais reste tracé pour la direction.
 */
const CartLine = ({ line, idx, lineBadges, onUpdateQty, onRemove, onSetRabais }) => {
  const thumb = stockPhotoUrl(line.thumbnail_url);
  const rabais = Number(line.rabais_degat || 0);
  const net = Math.max(0, Number(line.prix_total || 0) - rabais);
  const [open, setOpen] = useState(false);
  const [amount, setAmount] = useState(rabais > 0 ? String(rabais) : '');
  const [motif, setMotif] = useState(line.rabais_degat_motif || '');

  const apply = () => {
    const val = Math.max(0, Math.round((parseFloat(amount) || 0) * 100) / 100);
    onSetRabais(idx, Math.min(val, Number(line.prix_total || 0)), motif.trim());
    setOpen(false);
  };
  const clear = () => {
    setAmount(''); setMotif('');
    onSetRabais(idx, 0, '');
    setOpen(false);
  };

  return (
    <div className="p-3 rounded-lg bg-black/30 border border-white/10 shrink-0 overflow-hidden">
      <div className="flex items-start gap-2">
        <div className="shrink-0 w-12 h-12 rounded-lg bg-black/40 border border-white/10 overflow-hidden flex items-center justify-center">
          {thumb ? (
            <img src={thumb} alt="" className="w-full h-full object-contain p-0.5" />
          ) : (
            <Package size={18} className="text-gray-500 opacity-60" />
          )}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <div className="text-sm font-medium flex-1 leading-snug min-w-0 text-white">
              <div className="truncate">{line.designation}</div>
              {line.produit_ref && !line.is_acompte && line.produit_ref !== 'HORS-STOCK' && (
                <div className="text-[10px] text-gray-400 font-mono truncate mt-0.5">{line.produit_ref}</div>
              )}
            </div>
            <LineBadge ctype={lineBadges[line.produit_ref] || line.lineType || 'ventes'} />
          </div>
          <div className="flex items-center justify-between mt-2 text-xs text-gray-300">
            {line.stock_magasin_id ? <MagasinBadge code={line.stock_magasin_id} size="sm" /> : <span className="text-gray-500">—</span>}
            <span>{fmt(line.prix_unitaire)} €</span>
          </div>
          <div className="flex items-center justify-between mt-2">
            <div className="flex items-center gap-2">
              <button type="button" onClick={() => onUpdateQty(idx, -1)} className="min-w-[40px] min-h-[40px] rounded-lg bg-white/10 text-white text-lg">−</button>
              <span className="min-w-[24px] text-center font-bold text-white">{line.quantite}</span>
              <button type="button" onClick={() => onUpdateQty(idx, 1)} className="min-w-[40px] min-h-[40px] rounded-lg bg-white/10 text-white text-lg">+</button>
            </div>
            <div className="flex items-center gap-2">
              <div className="text-right">
                {rabais > 0 ? (
                  <>
                    <span className="text-[11px] text-gray-500 line-through mr-1">{fmt(line.prix_total)} €</span>
                    <span className="font-bold text-white">{fmt(net)} €</span>
                  </>
                ) : (
                  <span className="font-bold text-white">{fmt(line.prix_total)} €</span>
                )}
              </div>
              <button type="button" onClick={() => onRemove(idx)} className="p-2 text-red-400"><Trash2 size={16} /></button>
            </div>
          </div>

          {onSetRabais && (
            <div className="mt-2">
              {!open ? (
                <button
                  type="button"
                  onClick={() => setOpen(true)}
                  className={`inline-flex items-center gap-1 px-2 py-1 rounded-md text-[11px] border ${rabais > 0 ? 'bg-amber-500/15 text-amber-300 border-amber-500/30' : 'bg-white/5 text-gray-300 border-white/10 hover:bg-white/10'}`}
                  data-testid={`pos-v2-rabais-btn-${idx}`}
                >
                  <Tag size={12} />
                  {rabais > 0 ? `Rabais dégât −${fmt(rabais)} €` : 'Rabais (produit dégradé)'}
                </button>
              ) : (
                <div className="rounded-lg border border-amber-500/30 bg-amber-500/5 p-2 space-y-2" data-testid={`pos-v2-rabais-form-${idx}`}>
                  <div className="flex items-center gap-2">
                    <input
                      type="number" min="0" step="0.01" inputMode="decimal"
                      value={amount} onChange={(e) => setAmount(e.target.value)}
                      placeholder="Montant du rabais (€)" autoFocus
                      className="flex-1 input-dark text-xs py-1.5" data-testid={`pos-v2-rabais-amount-${idx}`}
                    />
                    <span className="text-[11px] text-gray-400">€</span>
                  </div>
                  <input
                    type="text" value={motif} onChange={(e) => setMotif(e.target.value)}
                    placeholder="Motif (ex. rayure, boîte abîmée) — optionnel"
                    className="w-full input-dark text-xs py-1.5" data-testid={`pos-v2-rabais-motif-${idx}`}
                  />
                  <p className="text-[10px] text-amber-300/80">Rabais libre, sans plafond, pour un produit dégradé. Il n'affecte pas la remise.</p>
                  <div className="flex gap-2">
                    <button type="button" onClick={apply} className="flex-1 py-1.5 rounded-md bg-amber-600 text-white text-xs font-semibold hover:bg-amber-500" data-testid={`pos-v2-rabais-apply-${idx}`}>Appliquer</button>
                    {rabais > 0 && <button type="button" onClick={clear} className="px-3 py-1.5 rounded-md bg-white/10 text-gray-200 text-xs hover:bg-white/20">Retirer</button>}
                    <button type="button" onClick={() => setOpen(false)} className="px-3 py-1.5 rounded-md bg-white/5 text-gray-400 text-xs hover:bg-white/10">Annuler</button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const PosV2CartPanel = ({ cart, lineBadges, onUpdateQty, onRemove, onSetRabais }) => (
  <section className="rounded-xl border border-white/10 bg-[#151820] p-3 flex flex-col min-h-[420px]">
    <div className="flex items-center gap-2 mb-2 font-bold text-sm text-white">
      <ShoppingCart size={18} className="text-red-500" />
      Panier ({cart.length})
    </div>
    <div className="flex-1 overflow-y-auto flex flex-col gap-2 pr-1 pb-1">
      {cart.map((line, idx) => (
        <CartLine
          key={`${line.produit_ref}-${line.stock_magasin_id || 'x'}-${idx}`}
          line={line} idx={idx} lineBadges={lineBadges}
          onUpdateQty={onUpdateQty} onRemove={onRemove} onSetRabais={onSetRabais}
        />
      ))}
      {!cart.length && <p className="text-gray-500 text-sm py-8 text-center">Panier vide — catalogue ou mode Réparation</p>}
    </div>
  </section>
);

export default PosV2CartPanel;
